import { c as _c } from "react/compiler-runtime";
import type { Language, Locale, OnboardingRequirement, OnboardingSessionConfig } from '@onefootprint/request-types';
import { useState } from 'react';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import ConfirmContinueOnDesktop from './components/confirm-continue-on-desktop';
import NewTabTransfer from './components/new-tab-transfer';
import QrTransfer from './components/qr-transfer';
import SmsTransfer from './components/sms-transfer';
import type { TransferMode, TransferRequirements } from './types';
import hasAppclipSupport from './utils/has-appclip-support';

// When state is 'transfer', this component branches on transferMode.
// The other two states are user-driven, determined by user actions whilst in a transfer flow.
type TransferState = 'transfer' | 'confirm-desktop' | 'new-tab-for-liveness';
export type TransferProps = {
  authToken: string;
  device: DeviceInfo;
  config: OnboardingSessionConfig;
  isInIframe: boolean;
  l10n: {
    locale?: Locale;
    language: Language;
  };
  missingRequirements: TransferRequirements;
  missingHandoffRequirements: OnboardingRequirement[];
  sandboxIdDocOutcome?: string;
  transferMode: Exclude<TransferMode, 'skip'>;
  fromContinueOnMobile: boolean;
  onDone: () => void;
};
const Transfer = t0 => {
  const $ = _c(52);
  const {
    authToken,
    device,
    config,
    isInIframe,
    l10n,
    missingRequirements,
    missingHandoffRequirements,
    sandboxIdDocOutcome,
    transferMode,
    fromContinueOnMobile,
    onDone
  } = t0;
  const [state, setState] = useState("transfer");
  let t1;
  if ($[0] !== missingHandoffRequirements) {
    t1 = hasAppclipSupport(missingHandoffRequirements);
    $[0] = missingHandoffRequirements;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const appclipSupported = t1;
  const needsNewTabForLiveness = !!missingRequirements.liveness && isInIframe && device.hasSupportForWebauthn;
  if (state === "new-tab-for-liveness") {
    let t2;
    if ($[2] !== authToken || $[3] !== config || $[4] !== device || $[5] !== fromContinueOnMobile || $[6] !== l10n || $[7] !== missingRequirements || $[8] !== onDone || $[9] !== sandboxIdDocOutcome) {
      t2 = <NewTabTransfer authToken={authToken} config={config} device={device} l10n={l10n} missingRequirements={missingRequirements} hasAppclipSupport={false} sandboxIdDocOutcome={sandboxIdDocOutcome} fromContinueOnMobile={fromContinueOnMobile} onDone={onDone} />;
      $[2] = authToken;
      $[3] = config;
      $[4] = device;
      $[5] = fromContinueOnMobile;
      $[6] = l10n;
      $[7] = missingRequirements;
      $[8] = onDone;
      $[9] = sandboxIdDocOutcome;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    return t2;
  }
  if (state === "confirm-desktop") {
    let t2;
    if ($[11] !== needsNewTabForLiveness || $[12] !== onDone) {
      t2 = () => {
        if (needsNewTabForLiveness) {
          setState("new-tab-for-liveness");
        } else {
          onDone();
        }
      };
      $[11] = needsNewTabForLiveness;
      $[12] = onDone;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    let t3;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = () => setState("transfer");
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[15] !== t2) {
      t4 = <ConfirmContinueOnDesktop onConfirmDesktop={t2} onBackToMobile={t3} />;
      $[15] = t2;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    return t4;
  }
  let t2;
  if ($[17] !== missingRequirements.documents) {
    t2 = missingRequirements.documents.length > 0 && !missingRequirements.documents.some(_temp);
    $[17] = missingRequirements.documents;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  const shouldWarnOnDesktop = t2;
  let t3;
  if ($[19] !== needsNewTabForLiveness || $[20] !== onDone || $[21] !== shouldWarnOnDesktop) {
    t3 = () => {
      if (shouldWarnOnDesktop) {
        setState("confirm-desktop");
      } else {
        if (needsNewTabForLiveness) {
          setState("new-tab-for-liveness");
        } else {
          onDone();
        }
      }
    };
    $[19] = needsNewTabForLiveness;
    $[20] = onDone;
    $[21] = shouldWarnOnDesktop;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  const handleContinueOnDesktop = t3;
  if (transferMode === "qr") {
    let t4;
    if ($[23] !== authToken || $[24] !== config || $[25] !== device || $[26] !== fromContinueOnMobile || $[27] !== handleContinueOnDesktop || $[28] !== l10n || $[29] !== missingRequirements || $[30] !== onDone || $[31] !== sandboxIdDocOutcome) {
      t4 = <QrTransfer authToken={authToken} config={config} device={device} l10n={l10n} missingRequirements={missingRequirements} sandboxIdDocOutcome={sandboxIdDocOutcome} onContinueOnDesktop={handleContinueOnDesktop} fromContinueOnMobile={fromContinueOnMobile} onDone={onDone} />;
      $[23] = authToken;
      $[24] = config;
      $[25] = device;
      $[26] = fromContinueOnMobile;
      $[27] = handleContinueOnDesktop;
      $[28] = l10n;
      $[29] = missingRequirements;
      $[30] = onDone;
      $[31] = sandboxIdDocOutcome;
      $[32] = t4;
    } else {
      t4 = $[32];
    }
    return t4;
  }
  if (transferMode === "sms") {
    let t4;
    if ($[33] !== authToken || $[34] !== config || $[35] !== device || $[36] !== fromContinueOnMobile || $[37] !== l10n || $[38] !== missingRequirements || $[39] !== onDone || $[40] !== sandboxIdDocOutcome) {
      t4 = <SmsTransfer authToken={authToken} config={config} device={device} l10n={l10n} missingRequirements={missingRequirements} sandboxIdDocOutcome={sandboxIdDocOutcome} fromContinueOnMobile={fromContinueOnMobile} onDone={onDone} />;
      $[33] = authToken;
      $[34] = config;
      $[35] = device;
      $[36] = fromContinueOnMobile;
      $[37] = l10n;
      $[38] = missingRequirements;
      $[39] = onDone;
      $[40] = sandboxIdDocOutcome;
      $[41] = t4;
    } else {
      t4 = $[41];
    }
    return t4;
  }
  if (transferMode === "new-tab") {
    let t4;
    if ($[42] !== appclipSupported || $[43] !== authToken || $[44] !== config || $[45] !== device || $[46] !== fromContinueOnMobile || $[47] !== l10n || $[48] !== missingRequirements || $[49] !== onDone || $[50] !== sandboxIdDocOutcome) {
      t4 = <NewTabTransfer authToken={authToken} config={config} device={device} l10n={l10n} missingRequirements={missingRequirements} hasAppclipSupport={appclipSupported} sandboxIdDocOutcome={sandboxIdDocOutcome} fromContinueOnMobile={fromContinueOnMobile} onDone={onDone} />;
      $[42] = appclipSupported;
      $[43] = authToken;
      $[44] = config;
      $[45] = device;
      $[46] = fromContinueOnMobile;
      $[47] = l10n;
      $[48] = missingRequirements;
      $[49] = onDone;
      $[50] = sandboxIdDocOutcome;
      $[51] = t4;
    } else {
      t4 = $[51];
    }
    return t4;
  }
  return null;
};
export default Transfer;
function _temp(doc) {
  return doc.uploadSettings === "prefer_upload";
}